<template>
    <div class="">
        <p>
            <em>This price will be before any petrol costs or other expenses (determined below)</em>
        </p>

        <div class="form-group mb-4">
            <label>
                <strong>
                    Calculation
                </strong>
            </label>
            <div class="pricecalculation">
                <div v-if="rate !== null">
                    <div class="form-group">
                        <table class="table jobprice">
                            <tbody>
                                <tr v-for="(rateInfo, idx) in rate_information" :key="idx">
                                    <td>{{ rateInfo[0] }}</td>
                                    <td>£{{ rateInfo[1] }}</td>
                                </tr>
                            </tbody>

                            <tfoot>
                            <tr>
                                <td>
                                    <strong>
                                        Total
                                    </strong>
                                </td>
                                <td>
                                    <strong>
                                        £{{ rate?.total }}
                                    </strong>
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div v-else>
                    <p class="mb-0">
                        Please enter the details below to generate a price
                    </p>

                    <p
                        class="text-white mb-0 mt-2 bg-danger py-2 px-4 rounded-2"
                        v-if="internalContractId === null"
                    >
                        <strong>
                            Please select a contract
                        </strong>
                    </p>
                    <div v-if="internalVehicleId === null" class="alert alert-danger mt-2">
                        <p class="mb-0">
                            Please select a vehicle
                        </p>
                    </div>
                    <div v-if="internalMileage === 0" class="alert alert-danger mt-2">
                        <p class="mb-0">
                            <strong>
                                Please enter a Collection or Delivery address to compute a mileage
                            </strong>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="form-group"
            :class="{
                'mb-4': !hideAdBlue && !hideFuel,
            }"
        >
            <label>
                <strong>
                    Price
                </strong>
            </label>
            <div class="input-group">
                <span class="input-group-text">&pound;</span>
                <input
                    v-model="internalPrice"
                    id="price"
                    class="form-control"
                    placeholder="0.00"
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                />
            </div>
        </div>

        <div class="row" v-if="!hideFuel || !hideAdBlue">
            <div class="d-flex align-items-center">
                <div v-if="!hideFuel" class="me-2">
                    <div class="checkbox checkbox-primary d-flex align-items-center">
                        <input
                            type="checkbox"
                            id="price_includes_fuel"
                            name="price_includes_fuel"
                            v-model="priceIncludesFuel"
                        />
                        <label for="price_includes_fuel" style="font-size: 12px;" class="mb-0 ms-2">
                            <strong>
                                Price Includes Fuel
                            </strong>
                        </label>
                    </div>
                </div>
                <div v-if="!hideAdBlue">
                    <div class="checkbox checkbox-primary d-flex align-items-center">
                        <input
                            type="checkbox"
                            id="price_includes_adblue"
                            name="price_includes_adblue"
                            v-model="priceIncludesAdblue"
                        />
                        <label for="price_includes_adblue" style="font-size: 12px;" class="mb-0 ms-2">
                            <strong>
                                Price Includes Adblue
                            </strong>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import axios from 'axios';
import { toInteger } from 'lodash';
import { errorAlert } from '@/utils';

const props = defineProps({
    contract_id: {
        type: Number,
        required: false,
        default: null,
    },
    vehicle_id: {
        type: Number,
        required: false,
        default: null,
    },
    mileage: {
        type: [Number, String],
        required: false,
        default: 0,
    },
    price: {
        type: [Number, String],
        required: false,
        default: 0,
    },
    fuel: {
        type: Boolean,
        required: false,
        default: false,
    },
    adblue: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideFuel: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideAdBlue: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const emit = defineEmits([
    'update-price-only',
    'update-price',
    'update-adblue',
    'update-fuel',
]);

// Internal state mirrors (keep UI editable while props are source-of-truth)
const internalContractId = ref(props.contract_id ?? null);
const internalVehicleId = ref(props.vehicle_id ?? null);
const internalMileage = ref(Number(props.mileage) ?? 0);

const rate = ref(null);
const rate_information = ref(null);

const internalPrice = ref(props.price !== null ? props.price : '');
const priceEstimate = ref(null);

const priceIncludesFuel = ref(props.fuel);
const priceIncludesAdblue = ref(props.adblue);

// Helpers
const hasRequiredInputs = computed(() => {
    return (
        internalContractId.value !== null &&
        internalVehicleId.value !== null &&
        internalMileage.value !== 0 &&
        internalMileage.value !== null
    );
});

async function getPrice() {
    if (!hasRequiredInputs.value) return;

    const data = {
        contract_id: internalContractId.value,
        vehicle_id: internalVehicleId.value,
        mileage: internalMileage.value,
    };

    try {
        const response = await axios.post(route('api.movements.jobs.getPrice'), data);

        rate.value = response.data.rate;
        rate_information.value = response.data.rate_information;

        priceEstimate.value = response.data.rate.total;
        // internalPrice.value = response.data.rate.total ?? ''; - do not update

        emit('update-price', {
            price: internalPrice.value,
            rate: rate.value,
            rate_information: rate_information.value,
            price_includes_fuel: priceIncludesFuel.value,
            price_includes_adblue: priceIncludesAdblue.value,
        });
    } catch (error) {
        if (!error?.response) return;
        errorAlert(error, 'Error Generating Price');
    }
}

onMounted(() => {
    getPrice();
});

// --- Prop → internal mirrors ---
watch(
    () => props.contract_id,
    (val) => {
        internalContractId.value = val;
    }
);

watch(
    () => props.vehicle_id,
    (val) => {
        internalVehicleId.value = val;
    }
);

watch(
    () => props.mileage,
    (val) => {
        if (Number.isInteger(toInteger(props.mileage))) {
            internalMileage.value = val;
        }
    }
);

watch(
    () => props.price,
    (newVal) => {
        internalPrice.value = newVal || '';
    }
);

watch(internalContractId, () => {
    getPrice();
});

watch(internalVehicleId, () => {
    getPrice();
});

watch(internalMileage, (value) => {
    if (Number.isInteger(toInteger(value))) {
        getPrice();
    }
});

watch(internalPrice, (newVal) => {
    emit('update-price-only', newVal === '' ? null : newVal);
});

watch(priceIncludesAdblue, () => {
    emit('update-adblue', priceIncludesAdblue.value);
});

watch(priceIncludesFuel, () => {
    emit('update-fuel', priceIncludesFuel.value);
});
</script>
